import { useAuth } from "@hooks/useAuth";
import { useDataMutation } from "@hooks/useData/useDataMutation";
import useGzLog from "@hooks/useGzLog";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { videoToBase64 } from "@utils/imageToBase64";
import { uploadVideoToStream } from "@utils/uploadVideoToStream";
import { getPagination, selectData, softDelete, supabase } from "@utils/serverClient";
import { undefinedToNull } from "lib/utils";
import { useMemo, useState } from "react";
import { Video } from "types";
import { z } from "zod";

export const uploadVideoWithUrlSchema = z.object({
  name: z.string().min(1, { message: "Name is required" }),
  url: z.string().url(),
  org_id: z.string().optional(),
})

export const _uploadVideoWithUrl = (async (newRow: z.infer<typeof uploadVideoWithUrlSchema>) => {
  const { error } = await supabase.from("video").insert(newRow);

  if (error) throw new Error("Upload failed" + error?.message);

  return newRow;
})

const getVideosSchema = z.object({
  paginate: z.boolean().optional(),
  searchQuery: z.string().optional(),
  orgId: z.string(),
  from: z.number(),
  to: z.number(),
})

interface VideoUploadMetadata {
  name: string;
  extension?: string;
  org_id?: string;
  is_public?: boolean;
  type?: 'video';
}

interface VideoUploadData {
  data: {
    metadata: VideoUploadMetadata;
    file: File;
  };
}

async function _uploadVideo(data: VideoUploadData): Promise<Video> {
  return uploadVideoToStream({
    file: data.data.file,
    metadata: data.data.metadata,
  });
}

export const getVideos = (async ({ paginate, searchQuery, orgId, from, to }: z.infer<typeof getVideosSchema>) => {
  const { data, error } = paginate
    ? searchQuery
      ? await selectData({
        table: "video",
        columns: `*`,
      })
        .eq("org_id", orgId)
        .order("created_at", { ascending: false })
        .range(from, to)
        .textSearch("name", searchQuery)
      : await selectData({
        table: "video",
        columns: `*`,
      })
        .eq("org_id", orgId)
        .order("created_at", { ascending: false })
        .range(from, to)
    : await selectData({
      table: "video",
      columns: `*`,
    })
      .eq("org_id", orgId)
      .order("created_at", { ascending: false });

  if (error || !data) throw error;

  return data;
});

export function useVideoAssets() {
  const paginate = true
  const [pageSize, setPageSize] = useState(30);
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);

  const { organization } = useAuth()

  const queryClient = useQueryClient();

  const queryKey = ["videoAssets", searchQuery, paginate, organization?.id]
  const { log } = useGzLog()

  const { mutate: uploadAsset, isPending: isUploading } = useDataMutation({
    mutationFn: async (acceptedFiles: File[]) => {
      if (!organization?.id) return;
      if (acceptedFiles.length === 0) return;

      for (let i = 0; i < acceptedFiles.length; i++) {
        const acceptedFile = acceptedFiles[i];
        if (!acceptedFile) continue;

        const fileExtension = acceptedFile.name.split(".").pop()?.toLowerCase() ?? "";
        const fileName = acceptedFile.name.split(".")[0] ?? "";

        if (!["mp4", "webm", "mov"].includes(fileExtension)) {
          continue;
        }

        return _uploadVideo({
          data: {
            metadata: {
              name: fileName,
              org_id: organization?.id ?? "",
              extension: fileExtension,
            },
            file: acceptedFile,
          },
        });
      }
    },
    queryKeyToInvalidate: queryKey,
    errorMessage: "Failed to upload video",
    optimisticUpdateFn: (oldData: Video[] | undefined, files) => {
      return [...files.map<Video>(file => ({
        created_at: new Date().toISOString(),
        deleted: false,
        dimension: null,
        extension: file.name.split(".").pop() ?? "",
        id: "temp-" + Date.now(),
        name: file.name,
        org_id: organization?.id ?? "",
        tags: [],
        updated_at: null,
        url: URL.createObjectURL(file),
        thumbnail: null,
      })), ...(oldData ?? [])]
    },
    options: {
      onSuccess: (video) => {
        log({
          eventtype: "VideoUploaded",
          name: "VideoUploaded",
          payload: {
            videoUrl: video?.url ?? "undefined",
          },
        });
      },
      onError: (error) => {
        log({
          eventtype: "VideoUploadFailed",
          name: "VideoUploadFailed",
          payload: {
            errorMessage: error.message,
          },
        });
      },
    }
  })

  const {
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    data,
    status: assetsFetchStatus,
  } = useInfiniteQuery({
    queryKey,
    // refetchOnMount: false,
    queryFn: async ({ pageParam = 0 }) => {
      if (pageParam === undefined) return null;

      const { from, to } = getPagination(pageParam, pageSize);
      const orgId = organization?.id ?? null;
      if (!orgId) return null;

      try {
        return getVideos({
          paginate,
          searchQuery,
          from,
          to,
          orgId: organization?.id ?? "",
        });
      } catch (_) {
        return null;
      }
    },
    getNextPageParam: (_lastGroup, groups) =>
      (_lastGroup ?? [])?.length < pageSize ? undefined : groups.length,
    initialPageParam: 0,
  });

  const { mutate: deleteAsset } = useDataMutation({
    mutationFn: async (assetId: string) => {
      const { error, data } = await softDelete({
        table: "video",
      }).match({ id: assetId });
      if (error) throw error;
      return data;
    },
    queryKeyToInvalidate: queryKey,
    errorMessage: "Failed to delete asset",
    optimisticUpdateFn: (oldData: Video[] | undefined, assetId) => {
      if (!oldData) return oldData;
      return oldData.filter((asset) => asset.id !== assetId);
    },
    options: {
      onSuccess: (_, assetId) => {
        log({
          eventtype: "VideoDeleted",
          name: "VideoDeleted",
          payload: {
            videoId: assetId,
          },
        });
      },
      onError: (error, assetId) => {
        log({
          eventtype: "VideoDeleteFailed",
          name: "VideoDeleteFailed",
          payload: {
            videoId: assetId,
            errorMessage: error.message,
          },
        });
      }
    }
  })

  const { mutate: updateAsset, isPending: isUpdatingAsset } = useDataMutation({
    mutationFn: async ({ id, name, tags }: Pick<Video, 'name' | 'tags' | 'id'>) => {
      const { error } = await supabase
        .from("video")
        .update({
          name,
          tags,
        })
        .eq("id", id);

      if (error) {
        throw new Error(error.message);
      }
      return null;
    },
    queryKeyToInvalidate: queryKey,
    errorMessage: "Failed to update asset",
    optimisticUpdateFn: (oldData: Video[] | undefined, newAsset) => {
      if (!oldData) return oldData;
      return oldData.map((asset) =>
        asset.id === newAsset.id ? { ...asset, ...newAsset } : asset
      );
    },
    options: {
      onSuccess: (_, video) => {
        log({
          eventtype: "VideoUpdated",
          name: "VideoUpdated",
          payload: {
            videoId: video.id,
            videoName: video.name ?? "undefined",
          },
        });
      },
      onError: (error, video) => {
        log({
          eventtype: "VideoUpdateFailed",
          name: "VideoUpdateFailed",
          payload: {
            videoId: video.id,
            videoName: video.name ?? "undefined",
            errorMessage: error.message,
          },
        });
      },
    }
  });

  const { mutate: uploadAssetWithUrl } = useDataMutation({
    mutationFn: _uploadVideoWithUrl,
    queryKeyToInvalidate: queryKey,
    errorMessage: "Failed to upload video",
    optimisticUpdateFn: (oldData: Video[] | undefined, newVideo) => {
      return [{
        ...undefinedToNull(newVideo as Video),
        name: newVideo.name ?? "",
        org_id: newVideo.org_id ?? organization?.id ?? "",
        url: newVideo.url ?? "",
        id: 'temp-' + Date.now(),
        created_at: new Date().toISOString(),
      }, ...(oldData ?? [])]
    },
    options: {
      onSuccess: (video) => {
        log({
          eventtype: "VideoUploaded",
          name: "VideoUploaded",
          payload: {
            videoUrl: video?.url ?? "undefined",
          },
        });
      },
      onError: (error) => {
        log({
          eventtype: "VideoUploadFailed",
          name: "VideoUploadFailed",
          payload: {
            errorMessage: error.message,
          },
        });
      },
    }
  })

  function getAssets() {
    queryClient.invalidateQueries({ queryKey });
  }

  const assets = useMemo(() => {
    return data?.pages.flatMap((e) => e!) ?? [];
  }, [data])

  return {
    assets,
    assetsFetchStatus,
    getAssets,
    isUploading,
    uploadAsset,
    hasNextPage,
    pageSize,
    setPageSize,
    searchQuery,
    setSearchQuery,
    isFetchingNextPage,
    fetchNextPage,
    deleteAsset,
    isUpdatingAsset,
    updateAsset,
    uploadAssetWithUrl,
  }
};
